/**
 * `/teilnehmer/$personId` — eine Person.
 *
 * Hier stehen die Termine einzeln. Das ist der empfindlichste Bildschirm des Portals: eine
 * Anwesenheitsliste über einen namentlich genannten Menschen, meist einen Minderjährigen, neben
 * seinem Kontostand. Er ist deshalb absichtlich nüchtern — keine Ampel, keine Bewertung, kein
 * Platz für eine Notiz. Wer etwas festhalten will, tut das im Gespräch, nicht in dieser Akte.
 *
 * Der Aufruf dieser Seite wird mit Person und Betrachter protokolliert.
 */
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'

import { BAND_LABEL, detailQuery, zugangQuery } from '~/components/teilnehmer/api.ts'
import { Verpflichtung } from '~/components/teilnehmer/verpflichtung.tsx'
import { TechnicalErrorAlert } from '~/components/technical-error-alert.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card.tsx'
import { daysSince, formatDate } from '~/lib/format.ts'
import { formatMoneySigned } from '~/lib/money.ts'
import { cn } from '~/lib/utils.ts'

export const Route = createFileRoute('/teilnehmer/$personId')({
  component: TeilnehmerDetailSeite,
})

function TeilnehmerDetailSeite() {
  const { personId } = Route.useParams()
  const zugang = useQuery(zugangQuery())
  const darfSehen = zugang.data?.darfSehen === true
  const detail = useQuery(detailQuery(personId, darfSehen))

  if (zugang.isPending) {
    return <p className="text-sm text-muted-foreground">Zugang wird geprüft …</p>
  }
  if (zugang.isError) return <TechnicalErrorAlert error={zugang.error} />
  if (!zugang.data || !darfSehen) return <Verpflichtung zugang={zugang.data} />

  if (detail.isPending) return <p className="text-sm text-muted-foreground">wird geladen …</p>
  if (detail.isError) return <TechnicalErrorAlert error={detail.error} />
  const d = detail.data
  if (!d) return null

  /** Gruppiert nach Kennung, damit man sieht, wo jemand verlässlich ist und wo nicht. */
  const nachKennung = new Map<string, { da: number; gemeldet: number }>()
  for (const t of d.termine) {
    const e = nachKennung.get(t.kennung) ?? { da: 0, gemeldet: 0 }
    e.gemeldet += 1
    if (t.da) e.da += 1
    nachKennung.set(t.kennung, e)
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/teilnehmer" className="text-sm text-muted-foreground hover:underline">
          ← Teilnehmer
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">
          {d.nameVor} {d.nameNach}
        </h1>
        <p className="text-sm text-muted-foreground">
          {BAND_LABEL[d.band] ?? d.band}
          {d.pmoId ? ` · Mitgliedsnummer ${d.pmoId}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kennzahl
          begriff="Teilnahme"
          wert={d.quote === null ? 'nie eingeteilt' : `${d.quote} %`}
          fuss={d.quote === null ? '—' : `${d.anwesend} von ${d.gemeldet} Terminen`}
        />
        <Kennzahl
          begriff="Zuletzt da"
          wert={d.zuletztDa ? formatDate(d.zuletztDa) : '—'}
          fuss={d.zuletztDa ? `vor ${daysSince(d.zuletztDa)} Tagen` : 'keine Anwesenheit gemeldet'}
          betont={d.abbruch}
        />
        <Kennzahl
          begriff="Saldo"
          wert={formatMoneySigned(d.saldoCents)}
          fuss={d.rueckstand ? 'steht dem nächsten Event im Weg' : 'nichts offen'}
          betont={d.rueckstand}
        />
      </div>

      {d.abbruch ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          War regelmäßig dabei und ist seit zwei Monaten nicht mehr gekommen. Das ist eine Rechnung
          aus den gemeldeten Terminen, keine Beurteilung — der Grund steht nirgends und lässt sich
          nur erfragen.
        </p>
      ) : null}

      {nachKennung.size > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Wobei</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[...nachKennung.entries()]
              .sort((a, b) => b[1].gemeldet - a[1].gemeldet)
              .map(([kennung, z]) => (
                <div key={kennung} className="flex items-baseline justify-between gap-3 text-sm">
                  <span>{kennung}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {z.da} von {z.gemeldet}
                  </span>
                </div>
              ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Termine der letzten {d.tage} Tage</CardTitle>
        </CardHeader>
        <CardContent>
          {d.termine.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Es ist nichts gemeldet. Das heisst nicht, dass die Person nicht da war — nur, dass
              niemand sie abgehakt hat.
            </p>
          ) : (
            <ul className="divide-y">
              {d.termine.map((t) => (
                <li
                  key={`${t.kennung}-${t.datum}`}
                  className="flex items-baseline justify-between gap-3 py-2 text-sm"
                >
                  <span className="tabular-nums text-muted-foreground">{formatDate(t.datum)}</span>
                  <span className="min-w-0 flex-1 truncate px-3">{t.kennung}</span>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium',
                      t.da ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
                    )}
                  >
                    {t.da ? 'da' : 'nicht da'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Kennzahl({
  begriff,
  wert,
  fuss,
  betont,
}: {
  begriff: string
  wert: string
  fuss: string
  betont?: boolean
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs text-muted-foreground">{begriff}</p>
      <p
        className={cn(
          'mt-1 text-xl font-semibold tabular-nums',
          betont && 'text-amber-600 dark:text-amber-400',
        )}
      >
        {wert}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{fuss}</p>
    </div>
  )
}
